import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, apiErrorMessage } from '@lipaflow/shared'
import toast from 'react-hot-toast'
import type { StepProps, VerificationMethod } from '../types'
import { resolveLabels } from '../types'

export default function VerifyTenantStep({ tenantId, verticalLabels }: StepProps) {
  const queryClient = useQueryClient()
  const labels = resolveLabels(verticalLabels)
  const [method, setMethod] = useState<VerificationMethod>('phone_call')
  const [formData, setFormData] = useState({
    principal_name: '',
    principal_phone: '',
    principal_email: '',
    principal_title: labels.admin || 'Administrator',
    call_notes: '',
    document_url: '',
    registration_number: '',
  })

  const verifyMutation = useMutation({
    mutationFn: async () => {
      await api.post('/admin/wizard/verify-tenant', {
        tenant_id: tenantId,
        method,
        ...formData,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-wizard', tenantId] })
      toast.success('Customer verified!')
    },
    onError: (err: unknown) => {
      toast.error(apiErrorMessage(err, 'Failed to verify'))
    },
  })

  return (
    <div>
      <h2 className="text-xl font-semibold mb-4">Step 2: Check they are real</h2>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          verifyMutation.mutate()
        }}
        className="space-y-4"
      >
        <div>
          <label className="block text-sm font-medium text-ink mb-2">How you checked</label>
          <div className="flex space-x-4">
            {(['phone_call', 'document', 'site_visit', 'referral'] as VerificationMethod[]).map((m) => (
              <label key={m} className="flex items-center">
                <input
                  type="radio"
                  name="method"
                  value={m}
                  checked={method === m}
                  onChange={() => setMethod(m)}
                  className="mr-2"
                />
                {m.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
              </label>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-ink">{labels.admin} Name *</label>
            <input
              type="text"
              required
              value={formData.principal_name}
              onChange={(e) => setFormData({ ...formData, principal_name: e.target.value })}
              className="mt-1 block w-full border border-hairline rounded-md shadow-sm py-2 px-3 focus:ring-accent focus:border-accent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink">{labels.admin} Phone *</label>
            <input
              type="tel"
              required
              value={formData.principal_phone}
              onChange={(e) => setFormData({ ...formData, principal_phone: e.target.value })}
              className="mt-1 block w-full border border-hairline rounded-md shadow-sm py-2 px-3 focus:ring-accent focus:border-accent"
            />
          </div>
        </div>

        {method === 'phone_call' && (
          <div>
            <label className="block text-sm font-medium text-ink">Call notes</label>
            <textarea
              value={formData.call_notes}
              onChange={(e) => setFormData({ ...formData, call_notes: e.target.value })}
              rows={3}
              placeholder="Notes from verification call..."
              className="mt-1 block w-full border border-hairline rounded-md shadow-sm py-2 px-3 focus:ring-accent focus:border-accent"
            />
          </div>
        )}

        {method === 'document' && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-ink">Registration number</label>
              <input
                type="text"
                value={formData.registration_number}
                onChange={(e) => setFormData({ ...formData, registration_number: e.target.value })}
                className="mt-1 block w-full border border-hairline rounded-md shadow-sm py-2 px-3 focus:ring-accent focus:border-accent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-ink">Document link</label>
              <input
                type="url"
                value={formData.document_url}
                onChange={(e) => setFormData({ ...formData, document_url: e.target.value })}
                className="mt-1 block w-full border border-hairline rounded-md shadow-sm py-2 px-3 focus:ring-accent focus:border-accent"
              />
            </div>
          </div>
        )}

        <div className="flex justify-end pt-4">
          <button
            type="submit"
            disabled={verifyMutation.isPending}
            className="px-6 py-2 bg-accent text-on-accent rounded-button hover:bg-accent-hover disabled:opacity-50"
          >
            {verifyMutation.isPending ? 'Verifying...' : 'Mark as Verified'}
          </button>
        </div>
      </form>
    </div>
  )
}
